import { buildApiUrl } from '../config';

const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));

const apiRequest = async (endpoint, options = {}) => {
  const url = buildApiUrl(endpoint);
  const defaultHeaders = {
    'Content-Type': 'application/json',
    'Accept': 'application/json'
  };
  
  const config = {
    headers: { ...defaultHeaders, ...options.headers },
    ...options
  };
  
  let lastError;
  const maxRetries = options.maxRetries || 0;
  
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      if (attempt > 0) {
        const backoffDelay = 1000 * Math.pow(2, attempt - 1);
        await sleep(backoffDelay);
      }
      
      const response = await fetch(url, config);
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.mensaje || `HTTP ${response.status}: ${response.statusText}`);
      }
      
      const data = await response.json();
      return {
        success: true,
        data,
        error: data.error,
        mensaje: data.mensaje,
        status: response.status,
        attempts: attempt + 1
      };
      
    } catch (error) {
      lastError = error;
      
      if (attempt === maxRetries) {
        break;
      }
      
      if (error.name === 'TypeError' && error.message.includes('Failed to fetch')) {
        continue;
      }
      
      if (error.message.includes('401') || error.message.includes('403')) {
        break;
      }
    }
  }
  
  throw new Error(`Error en petición API después de ${maxRetries + 1} intentos: ${lastError.message}`);
};

const validatePromotionData = (promotionData) => {
  const errors = [];
  
  const titulo = promotionData.Titulo || promotionData.pTitulo;
  const imagen = promotionData.Imagen || promotionData.pImagen;
  const ubicacion = promotionData.Ubicacion || promotionData.pUbicacion;
  const descripcion = promotionData.Descripcion || promotionData.pDescripcion;
  const valorDescuento = promotionData.Valor_descuento || promotionData.pValor_descuento;
  const mes = promotionData.Mes || promotionData.pMes;
  const fechaInicio = promotionData.Fecha_inicio || promotionData.pFecha_inicio;
  const fechaFin = promotionData.Fecha_fin || promotionData.pFecha_fin;
  const estado = promotionData.Estado || promotionData.pEstado;
  
  if (!titulo || titulo.trim().length === 0) {
    errors.push('El título es requerido');
  }
  
  if (titulo && titulo.length > 100) {
    errors.push('El título no puede exceder 100 caracteres');
  }
  
  if (!valorDescuento) {
    errors.push('El valor de descuento es requerido');
  }
  
  const descuento = parseFloat(valorDescuento);
  if (isNaN(descuento) || descuento < 0 || descuento > 100) {
    errors.push('El valor de descuento debe estar entre 0 y 100');
  }
  
  if (!descripcion || descripcion.trim().length === 0) {
    errors.push('La descripción es requerida');
  }
  
  if (descripcion && descripcion.length > 500) {
    errors.push('La descripción no puede exceder 500 caracteres');
  }
  
  if (mes) {
    const mesNum = parseInt(mes);
    if (isNaN(mesNum) || mesNum < 1 || mesNum > 12) {
      errors.push('El mes debe estar entre 1 y 12');
    }
  }
  
  if (!fechaInicio) {
    errors.push('La fecha de inicio es requerida');
  }
  
  if (!fechaFin) {
    errors.push('La fecha de fin es requerida');
  }
  
  if (fechaInicio && fechaFin) {
    const inicio = parseDate(fechaInicio);
    const fin = parseDate(fechaFin);
    
    if (fin <= inicio) {
      errors.push('La fecha de fin debe ser posterior a la fecha de inicio');
    }
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
};

const parseDate = (dateString) => {
  if (!dateString) return new Date();
  
  const months = {
    'enero': '01', 'febrero': '02', 'marzo': '03', 'abril': '04',
    'mayo': '05', 'junio': '06', 'julio': '07', 'agosto': '08',
    'septiembre': '09', 'octubre': '10', 'noviembre': '11', 'diciembre': '12'
  };
  
  const regex = /(\d{2})\s+de\s+(\w+)\s+del\s+(\d{4})/;
  const match = dateString.match(regex);
  
  if (match) {
    const [, day, month, year] = match;
    const monthNumber = months[month.toLowerCase()];
    return new Date(`${year}-${monthNumber}-${day}T12:00:00`);
  }
  
  return new Date(dateString);
};

const formatDateForAPI = (dateString, type = 'start') => {
  if (!dateString) return '';
  const date = parseDate(dateString);
  if (!date || isNaN(date.getTime())) return '';
  
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const time = type === 'end' ? '23:59:59' : '06:00:00';
  
  return `${year}-${month}-${day} ${time}`;
};

const promotionService = {
  getAll: async (filters = {}) => {
    const queryParams = new URLSearchParams();
    
    if (filters.page) queryParams.append('page', filters.page);
    if (filters.limit) queryParams.append('limit', filters.limit);
    if (filters.search) queryParams.append('search', filters.search);
    if (filters.fechaDesde) queryParams.append('fechaDesde', filters.fechaDesde);
    if (filters.fechaHasta) queryParams.append('fechaHasta', filters.fechaHasta);
    
    const queryString = queryParams.toString();
    const endpoint = queryString ? `promociones?${queryString}` : 'promociones';
    
    return apiRequest(endpoint);
  },
  
  getById: async (id) => {
    if (!id) {
      throw new Error('ID de promoción es requerido');
    }
    
    const result = await apiRequest(`promocion/${id}`);
    return {
      ...result,
      data: result.data?.promocion || result.data
    };
  },
  
  create: async (promotionData) => {
    const validation = validatePromotionData(promotionData);
    
    if (!validation.isValid) {
      throw new Error(`Datos inválidos: ${validation.errors.join(', ')}`);
    }
    
    const dataToCreate = {
      pTitulo: promotionData.Titulo || promotionData.pTitulo,
      pImagen: promotionData.Imagen || promotionData.pImagen || null,
      pFecha_inicio: formatDateForAPI(promotionData.Fecha_inicio || promotionData.pFecha_inicio, 'start'),
      pFecha_fin: formatDateForAPI(promotionData.Fecha_fin || promotionData.pFecha_fin, 'end'),
      pUbicacion: promotionData.Ubicacion || promotionData.pUbicacion || '',
      pMes: promotionData.Mes || promotionData.pMes || '',
      pDescripcion: promotionData.Descripcion || promotionData.pDescripcion,
      pValor_descuento: promotionData.Valor_descuento || promotionData.pValor_descuento,
      pEstado: promotionData.Estado || promotionData.pEstado || 'activa'
    };
    
    return apiRequest('promocion', {
      method: 'POST',
      body: JSON.stringify(dataToCreate)
    });
  },
  
  update: async (id, promotionData) => {
    if (!id) {
      throw new Error('ID de promoción es requerido para actualizar');
    }
    
    const validation = validatePromotionData(promotionData);
    
    if (!validation.isValid) {
      throw new Error(`Datos inválidos: ${validation.errors.join(', ')}`);
    }
    
    const dataToUpdate = {
      pTitulo: promotionData.Titulo || promotionData.pTitulo,
      pImagen: promotionData.Imagen || promotionData.pImagen || null,
      pFecha_inicio: formatDateForAPI(promotionData.Fecha_inicio || promotionData.pFecha_inicio, 'start'),
      pFecha_fin: formatDateForAPI(promotionData.Fecha_fin || promotionData.pFecha_fin, 'end'),
      pUbicacion: promotionData.Ubicacion || promotionData.pUbicacion || '',
      pMes: promotionData.Mes || promotionData.pMes || '',
      pDescripcion: promotionData.Descripcion || promotionData.pDescripcion,
      pValor_descuento: promotionData.Valor_descuento || promotionData.pValor_descuento,
      pEstado: promotionData.Estado || promotionData.pEstado || 'activa'
    };
    
    return apiRequest(`promocion/${id}`, {
      method: 'PUT',
      body: JSON.stringify(dataToUpdate)
    });
  },
  
  delete: async (id) => {
    if (!id) {
      throw new Error('ID de promoción es requerido');
    }
    
    return apiRequest(`promocion/${id}`, {
      method: 'DELETE'
    });
  },
  
  uploadImageAndCreatePromotion: async (promotionData, imageFile, options = {}) => {
    try {
      let imageUrl = null;
      
      if (imageFile) {
        const imageService = (await import('./imageService')).default;
        const uploadResult = await imageService.upload(imageFile, {
          onProgress: options.onImageProgress,
          maxRetries: 3
        });
        
        if (uploadResult.success) {
          imageUrl = uploadResult.url;
        } else {
          throw new Error('Error al subir la imagen');
        }
      }
      
      const promotionDataWithImage = {
        ...promotionData,
        pImagen: imageUrl
      };
      
      return await promotionService.create(promotionDataWithImage);
      
    } catch (error) {
      throw new Error(`Error al crear promoción con imagen: ${error.message}`);
    }
  },
  
  uploadImageAndUpdatePromotion: async (id, promotionData, imageFile, options = {}) => {
    try {
      let imageUrl = promotionData.Imagen || promotionData.pImagen;
      
      if (imageFile) {
        const imageService = (await import('./imageService')).default;
        const uploadResult = await imageService.upload(imageFile, {
          onProgress: options.onImageProgress,
          maxRetries: 3
        });
        
        if (uploadResult.success) {
          imageUrl = uploadResult.url;
        } else {
          throw new Error('Error al subir la imagen');
        }
      }
      
      const promotionDataWithImage = {
        ...promotionData,
        pImagen: imageUrl
      };
      
      return await promotionService.update(id, promotionDataWithImage);
      
    } catch (error) {
      throw new Error(`Error al actualizar promoción con imagen: ${error.message}`);
    }
  },
  
  validatePromotion: validatePromotionData,
  formatDateForAPI,
  parseDate
};

export default promotionService;
